import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Wifi, WifiOff, RefreshCw, AlertCircle } from "lucide-react";
import { checkApiHealth } from "@/services/authService";

interface ApiStatusProps {
  className?: string;
}

type ApiStatus = "online" | "offline" | "slow" | "error" | "checking";

export const ApiStatus = ({ className = "" }: ApiStatusProps) => {
  const [status, setStatus] = useState<ApiStatus>("checking");
  const [isChecking, setIsChecking] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const checkApiStatus = async () => {
    setIsChecking(true);
    setStatus("checking");
    setErrorMessage(null);

    const startTime = Date.now();

    try {
      const health = await checkApiHealth();
      const responseTime = Date.now() - startTime;

      if (health) {
        if (responseTime > 10000) {
          setStatus("slow");
        } else {
          setStatus("online");
        }
      } else {
        setStatus("error");
        setErrorMessage("API retornou erro");
      }
    } catch (error) {
      setStatus("offline");
      setErrorMessage(
        error instanceof Error ? error.message : "Erro de conexão"
      );
    } finally {
      setIsChecking(false);
    }
  };

  // Verificar status ao carregar o componente
  useEffect(() => {
    checkApiStatus();
  }, []);

  // Verificar status a cada 30 segundos
  useEffect(() => {
    const interval = setInterval(checkApiStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = () => {
    if (status === "checking") {
      return <RefreshCw className="h-3 w-3 animate-spin" />;
    }
    if (status === "online" || status === "slow") {
      return <Wifi className="h-3 w-3" />;
    }
    if (status === "offline") {
      return <WifiOff className="h-3 w-3" />;
    }
    if (status === "error") {
      return <AlertCircle className="h-3 w-3" />;
    }
    return <AlertCircle className="h-3 w-3" />;
  };

  const getStatusText = () => {
    if (status === "checking") {
      return "Verificando...";
    }
    if (status === "online") {
      return "API Online";
    }
    if (status === "slow") {
      return "API Lenta";
    }
    if (status === "offline") {
      return "API Offline";
    }
    if (status === "error") {
      return "API Offline";
    }
    return "Status Desconhecido";
  };

  const getStatusColor = () => {
    if (status === "online") {
      return "bg-green-500 hover:bg-green-600";
    }
    if (status === "slow") {
      return "bg-yellow-500 hover:bg-yellow-600";
    }
    if (status === "offline" || status === "error") {
      return "bg-red-500 hover:bg-red-600";
    }
    return "bg-gray-500 hover:bg-gray-600";
  };

  return (
    <div className={`flex flex-col items-center gap-2 ${className}`}>
      <div className="flex items-center gap-2">
        <Badge
          className={`${getStatusColor()} text-white border-0 flex items-center gap-1`}
        >
          {getStatusIcon()}
          <span className="text-xs">{getStatusText()}</span>
        </Badge>

        <Button
          variant="ghost"
          size="sm"
          onClick={checkApiStatus}
          disabled={isChecking}
          className="h-6 px-2 text-xs"
        >
          <RefreshCw
            className={`h-3 w-3 ${isChecking ? "animate-spin" : ""}`}
          />
        </Button>
      </div>

      {errorMessage && (status === "offline" || status === "error") && (
        <div className="text-xs text-red-500 text-center max-w-xs">
          {errorMessage}
        </div>
      )}
    </div>
  );
};
